/**
 * Device Detection Utilities for KeepSpatial
 */

/**
 * Detect if running on iOS
 * @returns {boolean}
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Detect if running on Android
 * @returns {boolean}
 */
export function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

/**
 * Detect if AR Quick Look is supported (iOS Safari only)
 * Excludes non-Safari iOS browsers (Chrome, Firefox, Opera) that report
 * `rel="ar"` support via WebKit but cannot actually launch Quick Look.
 * @returns {boolean}
 */
export function supportsARQuickLook() {
  const ua = navigator.userAgent;
  if (/CriOS|FxiOS|OPiOS|OPT\//i.test(ua)) {
    return false;
  }
  const a = document.createElement('a');
  return a.relList && a.relList.supports && a.relList.supports('ar');
}

/**
 * Detect if Scene Viewer is supported (Android)
 * @returns {boolean}
 */
export function supportsSceneViewer() {
  return isAndroid();
}

/**
 * Detect if WebXR is supported
 * @returns {Promise<boolean>}
 */
export async function supportsWebXR() {
  if (!navigator.xr) {
    return false;
  }

  try {
    return await navigator.xr.isSessionSupported('immersive-ar');
  } catch {
    return false;
  }
}

/**
 * Get the best AR mode for current device
 * @returns {'quick-look' | 'scene-viewer' | 'webxr' | 'none'}
 */
export function getBestARMode() {
  if (supportsARQuickLook()) {
    return 'quick-look';
  }

  if (supportsSceneViewer()) {
    return 'scene-viewer';
  }

  // WebXR check is async, so we return 'none' here
  // The component will check WebXR support separately if needed
  return 'none';
}
